import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import {
  Consumer,
  ConsumerInput,
  FormNine,
  FormNineGrid,
} from '@/features/formNine/types';
import { FiltersGrid, Pagination } from '@/features/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

export const formNineApi = createApi({
  reducerPath: 'formNineApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/consumers-without-nomination`,
  }),
  tagTypes: [
    'FormNines',
    'FormNine',
    'FormNineValidRecords',
    'FormNineInvalidRecords',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getFormNines: builder.infiniteQuery<
      FormNineGrid[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      providesTags: () => ['FormNines', 'FormNine'],
    }),
    getFormNineValidRecords: builder.infiniteQuery<
      Consumer[],
      FiltersGrid & { consumersInformationId: string },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg: { consumersInformationId, ...args }, pageParam }) => {
        return {
          url: `/${consumersInformationId}/valid-records/query`,
          params: { ...args, ...pageParam },
        };
      },
      providesTags: () => ['FormNineValidRecords'],
    }),
    getFormNineInvalidRecords: builder.infiniteQuery<
      Consumer[],
      FiltersGrid & { consumersInformationId: string },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg: { consumersInformationId, ...args }, pageParam }) => {
        return {
          url: `/${consumersInformationId}/invalid-records/query`,
          params: { ...args, ...pageParam },
        };
      },
      providesTags: () => ['FormNineInvalidRecords'],
    }),
    getFormNineById: builder.query<FormNine, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: result => [{ type: 'FormNine', id: result?.id }],
    }),
    createFormNine: builder.mutation<string, FormData>({
      query: data => ({
        url: ``,
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
        data,
      }),
      invalidatesTags: id => [
        'FormNines',
        { type: 'FormNine', id },
        'FormNineValidRecords',
        'FormNineInvalidRecords',
      ],
    }),
    updateFormNineById: builder.mutation<string, FormData>({
      query: data => ({
        url: `/${data.get('id')}`,
        method: 'PUT',
        headers: { 'Content-Type': 'multipart/form-data' },
        data,
      }),
      invalidatesTags: id => [
        'FormNines',
        { type: 'FormNine', id },
        'FormNineValidRecords',
        'FormNineInvalidRecords',
      ],
    }),
    deleteFormNineById: builder.mutation<FormNine, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: result => [
        'FormNines',
        { type: 'FormNine', id: result?.id },
        'FormNineValidRecords',
        'FormNineInvalidRecords',
      ],
    }),
    deleteAllErrorsFormNineById: builder.mutation<FormNine, { formId: string }>(
      {
        query: ({ formId }) => ({
          url: `/${formId}/all-errors`,
          method: 'DELETE',
        }),
        invalidatesTags: result => [
          'FormNines',
          { type: 'FormNine', id: result?.id },
          'FormNineValidRecords',
          'FormNineInvalidRecords',
        ],
      },
    ),
    approveFormNine: builder.mutation<FormNine, string>({
      query: id => ({
        url: `/${id}/approve`,
        method: 'PUT',
      }),
      invalidatesTags: result => [
        'FormNines',
        { type: 'FormNine', id: result?.id },
        'FormNineValidRecords',
        'FormNineInvalidRecords',
      ],
    }),
    updateFormNineRecords: builder.mutation<FormNine, ConsumerInput>({
      query: data => ({
        url: `/records`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        { type: 'FormNine', id: result?.id },
        'FormNineValidRecords',
        'FormNineInvalidRecords',
      ],
    }),
    deleteFormNineRecords: builder.mutation<FormNine, string>({
      query: id => ({
        url: `/records`,
        method: 'DELETE',
        data: { records: [id] },
      }),
      invalidatesTags: result => [
        { type: 'FormNine', id: result?.id },
        'FormNineValidRecords',
        'FormNineInvalidRecords',
      ],
    }),
  }),
});

export const {
  useGetFormNineByIdQuery,
  useCreateFormNineMutation,
  useUpdateFormNineByIdMutation,
  useApproveFormNineMutation,
  useUpdateFormNineRecordsMutation,
  useDeleteFormNineRecordsMutation,
  useDeleteAllErrorsFormNineByIdMutation,
} = formNineApi;

export const useGetFormNinesInfiniteQuery = normalizeInfiniteQueryData(
  formNineApi.useGetFormNinesInfiniteQuery,
);

export const useGetFormNineValidRecordsInfiniteQuery =
  normalizeInfiniteQueryData(
    formNineApi.useGetFormNineValidRecordsInfiniteQuery,
  );

export const useGetFormNineInvalidRecordsInfiniteQuery =
  normalizeInfiniteQueryData(
    formNineApi.useGetFormNineInvalidRecordsInfiniteQuery,
  );

const getCachedFormNineByIdSelector = cachedItemByIdSelector(
  'formNineApi',
  'getFormNinesI',
);

export const useGetCachedFormNineByIdSelector = id =>
  useSelector(getCachedFormNineByIdSelector(id));
